import React from 'react';
import { Container, Typography, Box, Paper, Divider, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const impressumData = {
  company: {
    name: "Piovation GmbH",
    address: {
      street: "Zehentmeierstraße 8a",
      zip: "85630",
      city: "Grasbrunn",
      country: "Deutschland"
    },
    managingDirector: "Mazda Sabony",
    email: "m.sabony@piovation.com",
    websites: [
      "https://www.piovation.com",
      "https://www.piosphere.de",
      "https://www.piosphere.ai"
    ],
    phone: "+49 1520 8741767",
    register: {
      court: "Amtsgericht München",
      number: "HRB 264100"
    },
    vatID: "DE367131891"
  },
  legal: {
    impressum: {
      responsibility: "Angaben gemäß § 5 TMG"
    },
    liability: {
      content: "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.",
      links: "Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich."
    }
  },
  privacyPolicy: {
    statement: "Der Schutz Ihrer Daten hat für uns einen hohen Stellenwert. Die Nutzung unserer Website ist grundsätzlich ohne Angabe personenbezogener Daten möglich. Sofern besondere Services genutzt werden, kann eine Verarbeitung personenbezogener Daten erforderlich sein. Ist dies gesetzlich nicht gedeckt, holen wir Ihre Einwilligung ein. Die Verarbeitung erfolgt gemäß DSGVO und nationalem Recht.",
    controller: {
      name: "Piovation GmbH",
      email: "m.sabony@piovation.com",
      address: {
        street: "Zehentmeierstraße 8a",
        zip: "85630",
        city: "Grasbrunn",
        country: "Deutschland"
      }
    },
    dataCollected: [
      "Browsertyp und -version",
      "Betriebssystem",
      "Referrer-URL",
      "Datum und Uhrzeit des Zugriffs",
      "IP-Adresse",
      "Internet-Service-Provider",
      "Aufgerufene Seiten",
      "Sicherheitsrelevante Informationen zur Abwehr von Angriffen"
    ]
  }
};

export default function Impressum() {
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      py: 8
    }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 2,
              background: 'linear-gradient(45deg, #50C878, #4CAF50)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            Impressum
          </Typography>
          <MuiLink
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            ← Zurück zur Startseite
          </MuiLink>
        </Box>

        <Paper sx={{ 
          p: 4, 
          bgcolor: 'background.paper',
          borderRadius: 2,
          mb: 4
        }}>
          <Typography variant="h4" sx={{ mb: 3, color: 'primary.main' }}>
            {impressumData.legal.impressum.responsibility}
          </Typography>

          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Unternehmen
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{impressumData.company.name}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {impressumData.company.address.street}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {impressumData.company.address.zip} {impressumData.company.address.city}, {impressumData.company.address.country}
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Geschäftsführer:</strong> {impressumData.company.managingDirector}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>E-Mail:</strong> {' '}
            <MuiLink href={`mailto:${impressumData.company.email}`} color="primary">
              {impressumData.company.email}
            </MuiLink>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Telefon:</strong> {impressumData.company.phone}
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Websites:</strong>
          </Typography>
          <Box sx={{ mb: 3 }}>
            {impressumData.company.websites.map((website, index) => (
              <Typography key={index} variant="body1" sx={{ ml: 2 }}>
                <MuiLink href={website} target="_blank" rel="noopener noreferrer" color="primary">
                  {website}
                </MuiLink>
              </Typography>
            ))}
          </Box>

          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Registergericht:</strong> {impressumData.company.register.court}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Registernummer:</strong> {impressumData.company.register.number}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            <strong>Umsatzsteuer-ID:</strong> {impressumData.company.vatID}
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Haftungsausschluss
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            Haftung für Inhalte
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {impressumData.legal.liability.content}
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            Haftung für Links
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {impressumData.legal.liability.links}
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Datenschutz
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {impressumData.privacyPolicy.statement}
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            Verantwortliche Stelle
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{impressumData.privacyPolicy.controller.name}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {impressumData.privacyPolicy.controller.address.street}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {impressumData.privacyPolicy.controller.address.zip} {impressumData.privacyPolicy.controller.address.city}, {impressumData.privacyPolicy.controller.address.country}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            <strong>E-Mail:</strong> {' '}
            <MuiLink href={`mailto:${impressumData.privacyPolicy.controller.email}`} color="primary">
              {impressumData.privacyPolicy.controller.email}
            </MuiLink>
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Automatisch erhobene Daten
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            {impressumData.privacyPolicy.dataCollected.map((item, index) => (
              <Typography component="li" key={index} variant="body1" sx={{ mb: 0.5 }}>
                {item}
              </Typography>
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            © 2025 Piovation GmbH – All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
